// The ONE details panel (DESIGN D6 "Detail"). Clicking a tile opens that asset's panel in a drawer: a two-column table
// whose rows are, in this order, Issue · Current status · First failed · Last measured · Impact · Watched by · Coverage
// · Ran · Complete · Moved; then the recorded facts, the members, and the checks that LANDED on the asset - each check
// drawn once, here and nowhere else. Every asset's panel is in the DOM (hidden unless open), so a search of the page
// source finds every landed check exactly once. View-only: opening a panel acts on nothing.
import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import type { HealthGridAsset, HealthGridLine } from "./types";
import type { GridCtx } from "./ctx";
import { CheckTile, Contradiction, LineWord, RoleBadge, StateWord, triggerText, writeText } from "./Tiles";
import {
  anchorId,
  assetLook,
  countKinds,
  coverageText,
  downstreamOf,
  fmtAgeH,
  fmtUtc,
  hoursBetween,
  isTime,
  kindsText,
  lineLook,
  plural,
  recordedDate
} from "./present";

export interface PanelApi {
  openId: string | null;
  open: (id: string, opener?: HTMLElement | null) => void;
  close: () => void;
}

export const PanelContext = createContext<PanelApi>({ openId: null, open: () => undefined, close: () => undefined });
export const usePanel = () => useContext(PanelContext);

export const DRAWER_ID = "ph-drawer";
const panelTitleId = (id: string) => `ph-panel-title-${anchorId(id)}`;

/** The detail table's rows, in the order DESIGN D6 fixes. */
export const DETAIL_ROWS = [
  "Issue",
  "Current status",
  "First failed",
  "Last measured",
  "Impact",
  "Watched by",
  "Coverage",
  "Ran",
  "Complete",
  "Moved"
] as const;

function LineCell({ l }: { l: HealthGridLine }) {
  const lk = lineLook(l);
  const cites = Array.isArray(l.cites) ? l.cites : [];
  return (
    <>
      <LineWord l={l} />
      {l.reason ? <span className="ph-line-reason"> - {l.reason}</span> : null}
      <Contradiction lk={lk} />
      <span className="ph-cites">
        {cites.length > 0 ? `cites ${cites.join(", ")}` : "cites no check"}
        {isTime(l.measured_at_utc) ? ` · measured ${fmtUtc(l.measured_at_utc)}` : ""}
      </span>
    </>
  );
}

function List({ items, empty }: { items: ReactNode[]; empty: string }) {
  if (items.length === 0) return <>{empty}</>;
  return (
    <ul className="ph-plain-list">
      {items.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  );
}

function AssetPanel({ a, ctx, open }: { a: HealthGridAsset; ctx: GridCtx; open: boolean }) {
  const lk = assetLook(a);
  const placed = ctx.placement.landed.get(a.id) ?? a.tiles.map(tile => ({ tile, monitor: null }));
  const down = downstreamOf(ctx.data, a.id);
  const consequents = ctx.consequentsOf.get(a.id) ?? [];
  const age = hoursBetween(a.measured_at_utc, ctx.data.evaluated_at_utc);
  const sched = lineLook(a.lines.schedule);
  const moved = lineLook(a.lines.moved);
  // DESIGN-R2 B19: after the asset's own reason, the evidence its per-pipeline cites contributed to its state
  const evidence = Array.isArray(a.evidence) ? a.evidence : [];
  const issue =
    lk.kind === "fine" && evidence.length === 0 ? (
      "none"
    ) : (
      <>
        {lk.kind === "fine" ? "none from its own checks" : a.reason?.trim() || "the server gave no reason"}
        {a.unknown_cause && lk.kind === "unknown" ? <span className="ph-hint"> (cause: {a.unknown_cause.label})</span> : null}
        <Contradiction lk={lk} />
        {evidence.length > 0 ? (
          <ul className="ph-plain-list" data-testid="issue-evidence">
            {evidence.map((e, i) => (
              <li key={i} data-evidence-kind={e.kind}>
                evidence ({e.line}: {e.word}) - {e.reason?.trim() || "the server gave no reason"}
                <span className="ph-cites">{e.cites.length > 0 ? `cites ${e.cites.join(", ")}` : "cites no check"}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </>
    );
  const firstFailed = isTime(a.failing_since_utc)
    ? a.failing_since_is_floor
      ? `at least ${fmtUtc(a.failing_since_utc)} - the first recorded run of this check`
      : fmtUtc(a.failing_since_utc)
    : lk.kind === "problem"
      ? "not known"
      : "none recorded";
  const lastMeasured = isTime(a.measured_at_utc) ? `${fmtUtc(a.measured_at_utc)} · ${fmtAgeH(age)}` : "never measured - no time recorded";
  const impact = (
    <>
      {down.length > 0 ? `downstream: ${down.map(d => ctx.labelOf(d.id)).join(", ")}` : "nothing downstream in the grid"}
      {consequents.length > 0 ? ` · collapsed under it: ${consequents.map(c => ctx.labelOf(c.id)).join(", ")}` : ""}
    </>
  );
  const watched = (
    <List
      empty="nothing - no monitor covers this asset"
      items={a.monitors.map(id => {
        const m = ctx.monitorsById.get(id);
        return m ? (
          <>
            <code>{id}</code> - {triggerText(m)} · {writeText(m)}
          </>
        ) : (
          <>
            <code>{id}</code> - not in this answer
          </>
        );
      })}
    />
  );
  const coverage = (
    <List
      empty="no check lands here"
      items={placed.map(p => (
        <>
          <code>{p.tile.check}</code>: {coverageText(p.tile.coverage)}
          {p.tile.coverage?.note ? ` - ${p.tile.coverage.note}` : ""}
        </>
      ))}
    />
  );
  const rows: [(typeof DETAIL_ROWS)[number], ReactNode][] = [
    ["Issue", issue],
    [
      "Current status",
      <>
        <StateWord lk={lk} /> · {sched.word} · moved: {moved.word}
      </>
    ],
    ["First failed", firstFailed],
    ["Last measured", lastMeasured],
    ["Impact", impact],
    ["Watched by", watched],
    ["Coverage", coverage],
    ["Ran", <LineCell l={a.lines.ran} />],
    ["Complete", <LineCell l={a.lines.complete} />],
    ["Moved", <LineCell l={a.lines.moved} />]
  ];
  const recorded = [...(a.summary ? [{ ...a.summary, summary: true }] : []), ...a.facts.map(f => ({ ...f, summary: false }))];
  return (
    <section className="ph-panel" hidden={!open} data-panel={a.id} data-testid="asset-panel" aria-labelledby={panelTitleId(a.id)}>
      <header className="ph-panel-head">
        <h3 id={panelTitleId(a.id)} tabIndex={-1}>
          {a.label}
        </h3>
        <span className="ph-hint">
          {a.type} · {a.env}
          {a.section === "housekeeping" ? " · housekeeping" : ""}
          {a.hidden ? " · retired, hidden unless 'show retired' is on" : ""}
        </span>
      </header>
      <div className={`ph-panel-state ph-k-${lk.kind}`} data-kind={lk.kind} data-state={a.state}>
        <p>
          <StateWord lk={lk} /> <RoleBadge role={a.role} collapsedUnder={a.collapsed_under} labelOf={ctx.labelOf} />
        </p>
      </div>
      <div className="ph-table-wrap">
        <table className="ph-dtable" data-testid="asset-detail">
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} data-row={k}>
                <th scope="row">{k}</th>
                <td>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4 className="ph-panel-sub">Recorded facts</h4>
      {recorded.length === 0 ? (
        <p className="ph-hint">none recorded</p>
      ) : (
        <ul className="ph-plain-list" data-testid="asset-facts">
          {recorded.map((f, i) => (
            <li key={i}>
              <span className="ph-tag">{f.tag || "untagged"}</span> {f.summary ? <strong>summary: </strong> : null}
              {f.text} <span className="ph-hint">· recorded {recordedDate(f.at)}</span>
            </li>
          ))}
        </ul>
      )}
      {a.members.length > 0 ? (
        <>
          <h4 className="ph-panel-sub">Members ({a.members.length})</h4>
          <ul className="ph-cols" data-testid="panel-members">
            {a.members.map(m => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </>
      ) : null}
      <h4 className="ph-panel-sub">
        {placed.length === 0 ? "no check lands on this asset" : `${plural(placed.length, "check")} land here: ${kindsText(countKinds(placed.map(p => p.tile)))}`}
      </h4>
      <div className="ph-checks">
        {placed.map((p, i) => (
          <CheckTile key={`c-${i}`} tile={p.tile} from={p.monitor} />
        ))}
      </div>
    </section>
  );
}

/** The drawer holding every asset's panel; only the open one is shown. */
export function AssetDrawer({ ctx }: { ctx: GridCtx }) {
  const p = usePanel();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (p.openId === null) return;
    document.getElementById(panelTitleId(p.openId))?.focus();
    ref.current?.scrollTo?.({ top: 0 });
  }, [p.openId]);
  const openAsset = p.openId !== null ? ctx.byId.get(p.openId) : undefined;
  return (
    <aside
      id={DRAWER_ID}
      ref={ref}
      className="ph-drawer"
      hidden={openAsset === undefined}
      aria-label={openAsset ? `details of ${openAsset.label}` : "asset details"}
      data-testid="asset-drawer"
      onKeyDown={e => {
        if (e.key === "Escape") p.close();
      }}
    >
      <div className="ph-drawer-bar">
        <span className="ph-drawer-title">DETAILS</span>
        <button type="button" className="ph-close" onClick={p.close} data-testid="drawer-close">
          close <span aria-hidden="true">×</span>
        </button>
      </div>
      {ctx.data.grid.assets.map(a => (
        <AssetPanel key={a.id} a={a} ctx={ctx} open={a.id === p.openId} />
      ))}
    </aside>
  );
}
